import React, {createContext, useCallback, useContext, useMemo, useState} from 'react';
import {REGISTRY_SINGLETON} from '../scenes/Registry';
import {SIDE_MENU_IDS, useSideMenuStack} from './SideMenuStackContext';

const PolicyMarketSideMenuContext = createContext(null);

// Открыт ли рынок и какая карточка политики в нем раскрыта. Рынок открывают
// не только из самой панели, но и из шапки (кнопка рынка с миниатюрами),
// поэтому состояние живет над ними обоими
const usePolicyMarketMenuController = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const {topSideMenuId, bringSideMenuToFront} = useSideMenuStack();

  // Панель уже открыта и лежит поверх остальных
  const isMenuOnTop = isMenuOpen && topSideMenuId === SIDE_MENU_IDS.POLICY_MARKET;

  const closeMenu = useCallback(() => {
    REGISTRY_SINGLETON.getSoundManager().playSideMenuClick();
    setIsMenuOpen(false);
  }, []);

  const openMenu = useCallback(() => {
    const soundManager = REGISTRY_SINGLETON.getSoundManager();
    if (isMenuOnTop) {
      soundManager.playMenuButtonClick();
      return;
    }

    soundManager.playSideMenuClick();
    setIsMenuOpen(true);
    bringSideMenuToFront(SIDE_MENU_IDS.POLICY_MARKET);
  }, [isMenuOnTop, bringSideMenuToFront]);

  // Кнопка рынка: открытая карточка политики перекрывает список предложений,
  // поэтому нажатие при открытой карточке возвращает к списку, а при списке —
  // задвигает панель, даже если поверх лежит список игроков
  const toggleMarket = useCallback(() => {
    if (isMenuOpen && !selectedPolicy) {
      closeMenu();
      return;
    }
    openMenu();
    setSelectedPolicy(null);
  }, [isMenuOpen, selectedPolicy, closeMenu, openMenu]);

  return useMemo(() => ({
    isMenuOpen,
    isMenuOnTop,
    selectedPolicy,
    setSelectedPolicy,
    openMenu,
    closeMenu,
    toggleMarket,
  }), [isMenuOpen, isMenuOnTop, selectedPolicy, openMenu, closeMenu, toggleMarket]);
};

export const PolicyMarketSideMenuProvider = ({children}) => {
  const value = usePolicyMarketMenuController();

  return (
    <PolicyMarketSideMenuContext.Provider value={value}>
      {children}
    </PolicyMarketSideMenuContext.Provider>
  );
};

/**
 * Состояние рынка политик. Без провайдера (панель отрисована отдельно —
 * тесты, витрина) — свое локальное состояние панели
 */
export const usePolicyMarketSideMenu = () => {
  const context = useContext(PolicyMarketSideMenuContext);
  const local = usePolicyMarketMenuController();
  return context || local;
};
